import { useEffect, useMemo, useState } from 'react';
import { BusinessUserApi, TransactionApi } from '@recargas-dominicanas/core/api';
import type { IProduct, ITransaction } from '@recargas-dominicanas/core/types';
import { TextField, LoadingModal } from '@recargas-dominicanas/core/components';
import { formatCurrency, useForm, validators } from '@recargas-dominicanas/core/utils';
import { useBusiness, useSales, useSalesReport, useTransactions } from '@recargas-dominicanas/core/store';
import { TopupConfirmModal } from '../topup-confirm-modal/topup-confirm-modal.component';
import { TopupModal } from '../topup-modal/topup-modal.component';
import { ErrorModal } from '../error-modal/error-modal.component';
import { printTopup } from '../../utils/printTopup';

import { 
  ProductCard,
  ProductCardButtons,
  ProductCardContent,
  ProductCardFields,
  ProductCardInfo,
  ProductCardTitle
} from '../product-card';

import { style } from './topup.module.css';

interface Props {
  product: IProduct;
}

export function Topup(props: Props) {

  const { product } = props;

  const businessStore = useBusiness();
  const salesReports = useSalesReport();
  const transactions = useTransactions();
  const salesStore = useSales();
  const business = businessStore.get();
  const form = useForm({ number: '', amount: '' });
  const [enabledTransition, setEnabledTransition] = useState(false);
  const [transaction, setTransaction] = useState<ITransaction>();
  const [ownerName, setOwnerName] = useState<string>();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openLoadingModal, setOpenLoadingModal] = useState(false);
  const [openLoadingTopupModal, setOpenLoadingTopupModal] = useState(false);
  const [openSuccessfulModal, setOpenSuccessfulModal] = useState(false);
  const [openInvalidPhoneModal, setOpenInvalidPhoneModal] = useState(false);
  const [openInsufficientFundsModal, setOpenInsufficientFundsModal] = useState(false);
  const [openWrongCompanyModal, setOpenWrongCompanyModal] = useState(false);
  const [openDuplicatedModal, setOpenDuplicatedModal] = useState(false);
  const [openUnknownErrorModal, setOpenUnknownErrorModal] = useState(false);

  const numberFieldLabel = useMemo(() => {
    switch (product.category) {
      case 'mobile-topup': return 'Teléfono';
      case 'betting-topup': return 'Cuenta';
      default: return 'Contrato';
    }
  }, [product.id]);

  const numberFieldType = useMemo(() => {
    switch (product.category) {
      case 'mobile-topup': return 'tel';
      case 'betting-topup': return 'text';
      default: return 'number';
    }
  }, [product.id]);

  const numberFieldValidators = useMemo(() => (
    product.category === 'mobile-topup' 
      ? [validators.required, validators.phone] 
      : [validators.required]
  ), [product.id]);

  const amountFieldHint = useMemo(() => (
    product.min && product.max 
      ? `min. ${formatCurrency(product.min)} - máx. ${formatCurrency(product.max)}` 
      : undefined
  ), [product.id]);

  const amountFieldValidators = useMemo(() => (
    product.min && product.max 
      ? [validators.required, validators.min(product.min), validators.max(product.max)] 
      : []
  ), [product.id]);

  const invalidNumberModalTitle = useMemo(() => {
    switch (product.category) {
      case 'mobile-topup': return 'Número de teléfono invalido';
      case 'betting-topup': return 'Número de cuenta invalido';
      default: return 'Número de contrato invalido';
    }
  }, [product.id]);

  const invalidNumberModalDescription = useMemo(() => {
    switch (product.category) {
      case 'mobile-topup': return 'Revice el número de teléfono e intentelo denuevo.';
      case 'betting-topup': return 'Revice el número de cuenta e intentelo denuevo.';
      default: return 'Revice el número de contrato e intentelo denuevo.';
    }
  }, [product.id]);

  useEffect(() => {
    form.clear();
    setEnabledTransition(true);
    setTimeout(() => setEnabledTransition(false), 400);
  }, [product.id]);

  async function handleConfirm() {

    if (!form.isValid()) return;

    const productKeys: IProduct['key'][] = [
      'pasorapido:topup', 
      'edesur:topup', 
      'cepm:topup', 
      'ceb:topup'
    ];

    if (!productKeys.includes(product.key)) {
      setOwnerName(undefined);
      setOpenConfirmModal(true);
      return;
    }

    try {
      
      setOwnerName(undefined);
      setOpenLoadingTopupModal(true);

      const topup = await TransactionApi.getTopup(product.id, form.value.number);
      
      setOwnerName(topup.fullname);
      setOpenLoadingTopupModal(false);
      setOpenConfirmModal(true);

    } catch (err) {

      console.error(err);

      setOpenLoadingTopupModal(false);
      setOpenUnknownErrorModal(true);
    }
  }

  async function handleSend() {

    if (!form.isValid()) return;

    setOpenConfirmModal(false);
    setOpenLoadingModal(true);
    setOwnerName(undefined);

    try {

      const transaction = await BusinessUserApi.sendTransaction(
        product.id, 
        form.value.number, 
        parseInt(form.value.amount)
      );

      form.clear();
      salesReports.fetchCurrent();
      transactions.fetchGroupByDay(1, 50);
      salesStore.fetch();
      
      setOpenLoadingModal(false);
      setTransaction(transaction);
      setOpenSuccessfulModal(true);

    } catch (err: any) {

      setOpenLoadingModal(false);
      
      if (err?.response?.data === 'INVALID_PHONE') return setOpenInvalidPhoneModal(true);
      if (err?.response?.data === 'INSUFFICIENT_FUNDS') return setOpenInsufficientFundsModal(true);
      if (err?.response?.data === 'WRONG_COMPANY') return setOpenWrongCompanyModal(true);
      if (err?.response?.data === 'DUPLICATED') return setOpenDuplicatedModal(true);

      setOpenUnknownErrorModal(true);
    }
  }

  return (
    <ProductCard>
      <ProductCardTitle 
        title='Recarga'/>
      <ProductCardContent>
        <ProductCardInfo
          enabledTransition={enabledTransition} 
          product={product}/>
        <ProductCardFields enabledTransition={enabledTransition}>
          <TextField
            style={style.textField}
            formField={form.fields.number}
            label={numberFieldLabel}
            type={numberFieldType}
            validators={numberFieldValidators}/>
          <TextField
            style={style.textField}
            formField={form.fields.amount}
            type='number'
            label='Monto'
            onEnter={handleConfirm}
            hint={amountFieldHint}
            validators={amountFieldValidators}/>
        </ProductCardFields>
      </ProductCardContent>
      <ProductCardButtons
        onAccept={handleConfirm}
        onCancel={() => form.clear()}/>
      <TopupConfirmModal
        open={openConfirmModal}
        product={product}
        ownerName={ownerName}
        phone={form.value.number}
        amount={parseInt(form.value.amount)}
        onAccept={handleSend}
        onClose={() => setOpenConfirmModal(false)}/>
      <LoadingModal
        title='Realizando operación' 
        open={openLoadingModal}/>
      <LoadingModal
        title='Consultando datos' 
        open={openLoadingTopupModal}/>
      <TopupModal
        open={openSuccessfulModal}
        title='Recarga exitosa'
        transaction={transaction!}
        onPrint={() => printTopup(business, transaction!)}
        onClose={() => setOpenSuccessfulModal(false)}/>
      <ErrorModal
        open={openInvalidPhoneModal}
        title={invalidNumberModalTitle}
        description={invalidNumberModalDescription}
        onClose={() => setOpenInvalidPhoneModal(false)}/>
      <ErrorModal
        open={openInsufficientFundsModal}
        title='No posee balance suficiente'
        description='No posee balance suficiente para realizar esta operación.'
        onClose={() => setOpenInsufficientFundsModal(false)}/>
      <ErrorModal
        open={openWrongCompanyModal}
        title='Compañía incorrecta'
        description='Este número no pertenece a esta compañía, intente con otra compañía.'
        onClose={() => setOpenWrongCompanyModal(false)}/>
      <ErrorModal
        open={openDuplicatedModal}
        title='Recarga repetida'
        description='No se pueden realizar varias recarga a la vez a un mismo número. Vuelva a intentarlo despues de 5 minutos.'
        onClose={() => setOpenDuplicatedModal(false)}/>
      <ErrorModal
        open={openUnknownErrorModal}
        title='Ocurrio un error'
        description='Intentelo denuevo más tarde.'
        onClose={() => setOpenUnknownErrorModal(false)}/>
    </ProductCard>
  );
}
